import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

interface PricingFAQProps {
  className?: string;
}

interface FAQItem {
  question: string;
  answer: string;
}

const faqData: FAQItem[] = [
  {
    question: "How does the 14-day free trial work?",
    answer: "All plans come with a 14-day free trial with full access to all features. No credit card required to start. We do not require your credit card until you are on a paid subscription. You can cancel anytime during the trial period with no charges."
  },
  {
    question: "Can I change my plan later?",
    answer: "Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately, and you'll be charged or credited on a prorated basis."
  },
  {
    question: "What happens if I exceed my plan limits?",
    answer: "We'll notify you if you're approaching your limits. You can upgrade your plan or we'll work with you to find the best solution for your needs."
  },
  {
    question: "Is there a setup fee?",
    answer: "No, there are no setup fees or hidden charges. The pricing you see is exactly what you pay monthly."
  },
  {
    question: "How do payment processing fees work?",
    answer: "We charge a 1% service fee on invoices, plus standard payment processing fees: 2.85% + $0.30 for credit cards and 1% + $0.50 for eChecks. These fees are only charged when payments are processed."
  },
  {
    question: "Can I cancel my subscription?",
    answer: "Yes, you can cancel your subscription at any time. There are no long-term contracts or cancellation fees. Your account will remain active until the end of your current billing period."
  },
  {
    question: "Do you offer discounts for annual billing?",
    answer: "Currently, we only offer monthly billing. However, we're working on annual billing options with discounts. Stay tuned for updates!"
  },
  {
    question: "Do you offer discounts for non-profit programs?",
    answer: "Yes! Contact us so we can verify your 501(c)(3) status and we will provide a discount code for 20% off your subscription rate. Visit our contact page to get started."
  },
  {
    question: "What kind of support do you provide?",
    answer: "Essential and Advanced plans include standard support via email and chat. Enterprise plans include concierge support with priority response times and dedicated account management."
  },
  {
    question: "Is my data secure?",
    answer: "Yes, we use industry-standard encryption and security measures to protect your data. We backup our data every 24 hours to ensure your information is always safe. We use Stripe for all payment transactions, which provides secure, PCI-compliant payment processing."
  },
  {
    question: "Can I get a demo before signing up?",
    answer: "Absolutely! Contact our sales team to schedule a personalized demo, or start your free trial to explore all features hands-on."
  }
];

export const PricingFAQ: React.FC<PricingFAQProps> = ({ className = '' }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className={`py-16 px-4 ${className}`}>
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Have questions about our pricing? We've got answers.
          </p>
        </motion.div>

        <div className="space-y-4">
          {faqData.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <Card className="hover:shadow-lg transition-all duration-300 cursor-pointer border border-gray-200">
                <CardContent className="p-0">
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full p-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-semibold text-gray-800 pr-4">
                      {faq.question}
                    </span>
                    <div className="flex-shrink-0">
                      {openIndex === index ? (
                        <ChevronUp className="h-5 w-5 text-[#FA8C00]" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                  </button>
                  
                  <AnimatePresence>
                    {openIndex === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 pb-6 pt-0">
                          <p className="text-gray-600 leading-relaxed">
                            {faq.answer}
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center mt-12"
        >
          <p className="text-gray-600 mb-4">
            Still have questions? We're here to help.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center text-[#FA8C00] hover:text-[#FA8C00]/80 font-medium transition-colors"
          >
            Contact our team →
          </a>
        </motion.div>
      </div>
    </section>
  );
};